/**
 * App.js - Leu Budget du Génie
 * 
 * Version: 4.1.0 - Ajout refresh token automatique
 * 
 * IMPORTANT: Ce fichier ne contient AUCUNE référence à:
 * - ResetPasswordPage
 * - forgot-password
 * - reset-password
 * - Email/mot de passe
 */

import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ResetPage from "./pages/ResetPage";
import PreviewPage from "./pages/PreviewPage";
import DesignPreviewPage from "./pages/DesignPreviewPage";
import TurtleOpacityTest from "./pages/TurtleOpacityTest";
import { API_URL } from "./config/api";

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// État global pour éviter les refresh multiples simultanés
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Setup axios interceptor avec refresh automatique du token
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    
    // URLs qui ne doivent pas déclencher de refresh (incluant les checks de session)
    const noRefreshUrls = [
      '/auth/google/session', 
      '/auth/logout', 
      '/auth/refresh', 
      '/auth/check-session',  // Ajouté pour éviter la boucle
      '/auth/me',             // Ajouté pour éviter la boucle
      '/health'
    ];
    const isNoRefreshUrl = noRefreshUrls.some(url => originalRequest?.url?.includes(url));
    
    // Si c'est une 401 et qu'on peut tenter un refresh
    if (error?.response?.status === 401 && !isNoRefreshUrl && !originalRequest._retry) {
      
      // Si un refresh est déjà en cours, mettre la requête en file d'attente
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Tenter de rafraîchir le token
        console.log('[AUTH] Token expiré, tentative de refresh...');
        const refreshResponse = await axios.post(
          `${API}/auth/refresh`, 
          {}, 
          { withCredentials: true }
        );
        
        if (refreshResponse.status === 200) {
          console.log('[AUTH] Token rafraîchi avec succès');
          processQueue(null);
          
          // Réessayer la requête originale
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.log('[AUTH] Échec du refresh, session expirée');
        processQueue(refreshError);
        
        // NE PAS dispatch auth:logout ici pour éviter les boucles
        // Laisser le composant AuthWrapper gérer la redirection
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // Ne pas afficher de toast pour les requêtes d'authentification silencieuses
    const isBackgroundRequest = originalRequest?.url?.includes('/auth/me') || 
                                originalRequest?.url?.includes('/auth/check-session') ||
                                originalRequest?.url?.includes('/health');
    
    if (isBackgroundRequest) {
      return Promise.reject(error);
    }
    
    // Handle server errors avec toast
    if (error?.response) {
      const { status, data } = error.response;
      
      if (status === 422 && data?.errors) {
        const errorMessage = Array.isArray(data.errors) ? data.errors.join(', ') : 'Données invalides';
        toast.error(`Données invalides: ${errorMessage}`);
      }
      else if (status >= 500) {
        toast.error("Une erreur serveur s'est produite. Veuillez réessayer.");
      }
      else if (data?.detail && status !== 401 && status !== 403 && status !== 400) {
        toast.error(data.detail);
      }
    } 
    else if (error?.request) {
      if (!navigator.onLine) {
        toast.error("Pas de connexion internet. Vérifiez votre connexion.");
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth context wrapper
function AuthWrapper({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Vérifier l'authentification au montage et lors des changements d'URL
  useEffect(() => {
    // Ne pas vérifier si on est sur la page de callback avec un session_id
    const hash = window.location.hash || location.hash;
    if (hash.includes('session_id=')) {
      console.log('[AUTH] Session ID détecté dans URL, skip auth check');
      setLoading(false);
      return;
    }
    
    // Ne pas vérifier si on est déjà sur la page d'auth
    if (location.pathname === '/auth' || location.pathname === '/') {
      console.log('[AUTH] Page auth, skip auth check');
      setLoading(false);
      return;
    }
    
    checkAuth();
  }, [location.pathname]);

  // Vérifier le statut d'authentification (avec timeout pour éviter blocage sur iOS)
  const checkAuth = useCallback(async () => {
    // Timeout de sécurité pour iOS
    const timeoutId = setTimeout(() => {
      console.log('[AUTH] Timeout atteint, arrêt du chargement');
      setLoading(false);
      setUser(null);
    }, 10000); // 10 secondes max
    
    try {
      const response = await axios.get(`${API}/auth/check-session`, { 
        withCredentials: true,
        timeout: 8000 // 8 secondes
      });
      clearTimeout(timeoutId);
      
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.log('[AUTH] check-session échoué, tentative /auth/me');
      
      // Fallback sur /auth/me
      try {
        const meResponse = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          timeout: 8000
        });
        clearTimeout(timeoutId);
        setUser(meResponse.data);
      } catch (meError) {
        clearTimeout(timeoutId);
        console.log('[AUTH] Session non trouvée, utilisateur non connecté');
        setUser(null);
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  // Callback de succès d'authentification
  const handleAuthSuccess = useCallback((userData) => {
    setUser(userData);
    navigate('/dashboard');
  }, [navigate]);

  // Déconnexion
  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignorer les erreurs de déconnexion
    } finally {
      setUser(null);
      navigate('/');
      toast.success('Déconnexion réussie');
    }
  }, [navigate]);

  // État de chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <img 
            src="/logo.png" 
            alt="Leu Budget du Génie" 
            className="h-16 w-16 rounded-2xl shadow-md animate-pulse" 
          />
          <div className="text-lg font-medium text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  return children({ 
    user, 
    setUser, 
    onAuthSuccess: handleAuthSuccess, 
    onLogout: handleLogout,
    refreshAuth: checkAuth
  });
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster richColors position="top-center" />
        <BrowserRouter>
          <Routes>
            {/* Page d'accueil / Connexion */}
            <Route path="/" element={
              <ErrorBoundary>
                <AuthWrapper>
                  {({ user, onAuthSuccess }) => 
                    user ? <Navigate to="/dashboard" replace /> : <AuthPage />
                  }
                </AuthWrapper>
              </ErrorBoundary>
            } />

            {/* Dashboard - traite aussi le callback Google via le hash */}
            <Route path="/dashboard" element={
              <ErrorBoundary>
                <AuthWrapper>
                  {({ user, setUser, onLogout, onAuthSuccess }) => {
                    // Si l'URL contient un session_id, traiter le callback
                    if (window.location.hash.includes('session_id=')) {
                      return <AuthCallback onAuthSuccess={onAuthSuccess} />;
                    }
                    // Sinon, afficher le dashboard ou rediriger
                    return user ? 
                      <Dashboard user={user} setUser={setUser} onLogout={onLogout} /> : 
                      <Navigate to="/" replace />;
                  }}
                </AuthWrapper>
              </ErrorBoundary>
            } />

            {/* Page de reset usine (conservée - pas liée à l'auth) */}
            <Route path="/reset" element={<ResetPage />} />

            {/* Page de preview pour audit visuel */}
            <Route path="/preview" element={<PreviewPage />} />

            {/* Page de preview du nouveau design */}
            <Route path="/design-preview" element={<DesignPreviewPage />} />

            {/* Test d'opacité de la tortue */}
            <Route path="/turtle-test" element={<TurtleOpacityTest />} />

            {/* Redirection des anciennes URLs vers l'accueil */}
            <Route path="/forgot-password" element={<Navigate to="/" replace />} />
            <Route path="/reset-password" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Navigate to="/" replace />} />

            {/* 404 - Redirection vers l'accueil */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
